import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { CLUB_ID, THEME_COLOR } from "../config/club";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

export default function SessionSummaryModal({ session, onClose }) {
  const [players, setPlayers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const snap = await getDoc(
          doc(db, "clubs", CLUB_ID, "sessions", session.id, "summary", "final")
        );
        if (!snap.exists()) {
          setLoading(false);
          return;
        }
        const data = snap.data();
        const sortedPlayers = [...(data.players || [])].sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins;
          if (b.winRate !== a.winRate) return b.winRate - a.winRate;
          if (b.gamesPlayed !== a.gamesPlayed) return b.gamesPlayed - a.gamesPlayed;
          return a.losses - b.losses;
        });
        setSummary(data);
        setPlayers(sortedPlayers);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load summary", err);
        setLoading(false);
      }
    };
    loadSummary();
  }, [session.id]);

  let lastRank = 0;
  let lastStats = null;

  return (
    <div className="modal d-block animate-fade-in" style={{
      background: "rgba(0,0,0,0.65)",
      backdropFilter: "blur(6px)"
    }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div
          className="modal-content border-0 shadow-lg rounded-5 overflow-hidden"
          style={{
            background: "var(--card-bg)",
            color: "var(--text-color)"
          }}
        >

          {/* Header with Theme Background */}
          <div className="modal-header border-0 px-4 pt-4 pb-0">
            <div>
              <h4 className="fw-bold mb-0" style={{ color: THEME_COLOR }}>Session Analytics</h4>
              <p className="text-muted small mb-0">{session.name} • Final Results</p>
            </div>
            <button className="btn-close shadow-none" onClick={onClose} />
          </div>

          <div className="modal-body px-4 py-4">
            {loading ? (
              <div className="py-5 text-center">
                <div className="spinner-border spinner-border-sm mb-2" style={{ color: THEME_COLOR }}></div>
                <div className="text-muted small">Aggregating session data...</div>
              </div>
            ) : !summary ? (
              <div
                className="p-5 text-center rounded-4"
                style={{ background: "var(--surface-bg)" }}
              >
                <i className="bi bi-bar-chart text-muted fs-1"></i>
                <div className="text-muted mt-2 small">No summary data found for this session</div>
              </div>
            ) : (
              <>
                {/* MODERN STAT CARDS */}
                <div className="row g-3 mb-5">
                  <div className="col-6 col-md-6">
                    <div
                      className="p-3 rounded-4 shadow-sm text-center"
                      style={{
                        background: "var(--card-bg)",
                        border: "1px solid var(--border-color)"
                      }}
                    >
                      <div className="fw-bold fs-3" style={{ color: THEME_COLOR }}>{summary.totalPlayers}</div>
                      <div className="text-uppercase small fw-bold text-muted tracking-wider" style={{ fontSize: '0.65rem' }}>Total Players</div>
                    </div>
                  </div>
                  {/* <div className="col-6 col-md-4">
                    <div className="p-3 rounded-4 border-0 shadow-sm bg-white text-center">
                      <div className="fw-bold fs-3" style={{ color: THEME_COLOR }}>{summary.totalMatches}</div>
                      <div className="text-uppercase small fw-bold text-muted tracking-wider" style={{ fontSize: '0.65rem' }}>Matches Played</div>
                    </div>
                  </div> */}
                  <div className="col-6 col-md-6 d-none d-md-block">
                    <div className="p-3 rounded-4 border-0 shadow-sm text-white text-center" style={{ backgroundColor: THEME_COLOR }}>
                      <div className="fw-bold fs-3">{summary.totalMatches}</div>
                      <div className="text-uppercase small fw-bold opacity-75 tracking-wider" style={{ fontSize: '0.65rem' }}>Matches Played</div>
                    </div>
                  </div>
                </div>

                {/* CHARTS SECTION */}
                <div className="row g-4 mb-5">
                  <div className="col-md-6">
                    <div className="p-3 rounded-4 shadow-sm" style={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-color)"
                    }}>
                      <h6
                        className="fw-bold mb-3 small"
                        style={{ color: "var(--text-muted)" }}
                      >WINS DISTRIBUTION</h6>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={players}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                          <YAxis hide />
                          <Tooltip
                            cursor={{ fill: themeAlpha("05") }}
                            contentStyle={{
                              background: "var(--card-bg)",
                              border: "1px solid var(--border-color)",
                              borderRadius: "10px"
                            }}
                            labelStyle={{
                              color: "var(--text-color)",
                              fontWeight: 600
                            }}
                            itemStyle={{
                              color: "var(--text-muted)"
                            }}
                          />
                          <Bar dataKey="wins" fill={THEME_COLOR} radius={[4, 4, 0, 0]} barSize={30} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 rounded-4 shadow-sm" style={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-color)"
                    }}>
                      <h6
                        className="fw-bold mb-3 small"
                        style={{ color: "var(--text-muted)" }}
                      >GAMES COMPLETED</h6>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={players}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                          <YAxis hide />
                          <Tooltip
                            cursor={{ fill: themeAlpha("05") }}
                            contentStyle={{
                              background: "var(--card-bg)",
                              border: "1px solid var(--border-color)",
                              borderRadius: "10px"
                            }}
                            labelStyle={{
                              color: "var(--text-color)",
                              fontWeight: 600
                            }}
                            itemStyle={{
                              color: "var(--text-muted)"
                            }}
                          />
                          <Bar dataKey="gamesPlayed" fill={THEME_COLOR} radius={[4, 4, 0, 0]} barSize={30} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* STANDINGS TABLE */}
                <div
                  className="rounded-4 overflow-hidden shadow-sm"
                  style={{
                    background: "var(--card-bg)",
                    border: "1px solid var(--border-color)"
                  }}
                >
                  <div
                    className="p-3 border-bottom"
                    style={{ background: "var(--surface-bg)" }}
                  >
                    <h6 className="fw-bold mb-0 small text-uppercase tracking-wider">Leaderboard Standings</h6>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ background: "var(--surface-bg)" }}>
                        <tr className="small text-muted">
                          <th className="ps-4 border-0">RANK</th>
                          <th className="border-0">PLAYER</th>
                          <th className="text-center border-0">W</th>
                          <th className="text-center border-0">L</th>
                          <th className="text-center border-0">WIN RATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((p, index) => {
                          const isTie =
                            lastStats &&
                            p.wins === lastStats.wins &&
                            p.winRate === lastStats.winRate &&
                            p.gamesPlayed === lastStats.gamesPlayed &&
                            p.losses === lastStats.losses;

                          const rank = isTie ? lastRank : index + 1;

                          lastRank = rank;
                          lastStats = p;

                          return (
                            <tr key={p.playerId} className="align-middle">
                              <td className="ps-4 fw-bold text-muted">
                                {rank}
                                {isTie && <span className="ms-1 small">(tie)</span>}
                              </td>

                              <td className="fw-bold" style={{ color: "var(--text-color)" }}>{p.name}</td>

                              <td className="text-center">
                                <span className="badge rounded-pill bg-success-subtle text-success px-3">
                                  {p.wins}
                                </span>
                              </td>

                              <td className="text-center">
                                <span className="badge rounded-pill bg-danger-subtle text-danger px-3">
                                  {p.losses}
                                </span>
                              </td>

                              <td className="text-center">
                                <span className="fw-bold" style={{ color: THEME_COLOR }}>
                                  {p.winRate}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}

                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer border-0 p-4 pt-0">
            <button className="btn rounded-pill px-4 fw-bold"
              style={{
                background: "var(--surface-bg)",
                color: "var(--text-color)",
                border: "1px solid var(--border-color)"
              }} onClick={onClose}>
              Dismiss
            </button>
            <button className="btn rounded-pill px-4 fw-bold text-white shadow-sm" style={{ backgroundColor: THEME_COLOR }} onClick={() => window.print()}>
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}